import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  CheckCircle, Download, ArrowRight, ExternalLink,
  Expand, Minimize2, AlertTriangle, Loader2
} from 'lucide-react';
import api from '../../../api';
import toast from 'react-hot-toast';
import type { Issue, Question, QualityScore, PipelinePhase, ChangeLogEntry, DataPreviewRow, CleaningResults, SheetUnit } from './types';
import { adaptToCleaningResultsV2 } from './types';
import OverviewTab from './OverviewTab';
import IssuesTable from './IssuesTable';
import QuestionsPanel from './QuestionsPanel';
import ChangeLogTab from './ChangeLogTab';
import DataPreviewTab from './DataPreviewTab';
import CrossUnitTab from './CrossUnitTab';

const RECON_PILL_ID = '__reconciliation__';

interface CleaningResultsPanelProps {
  result: any;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onSendToSmartAnalyst: () => void;
  onSendToCouncil: () => void;
}

function mapIssuesFromResult(raw: any): Issue[] {
  const issues: Issue[] = [];
  const issuesFound = raw.issuesFound || [];
  const stakeholderQuestions = raw.stakeholderQuestions || [];

  const questionsByColumn: Record<string, number> = {};
  stakeholderQuestions.forEach((sq: any, i: number) => {
    (sq.affectedColumns || []).forEach((col: string) => { questionsByColumn[col] = i; });
  });

  issuesFound.forEach((item: any, i: number) => {
    const needsReview = item.status === 'needs_review' || item.status === 'cannot_fix';
    const matchingQIndex = questionsByColumn[item.column] ?? (needsReview ? i : -1);
    const linkedQId = needsReview && matchingQIndex >= 0 && matchingQIndex < stakeholderQuestions.length ? `Q-${matchingQIndex + 1}` : null;

    let status: Issue['status'] = 'AUTO_FIXED';
    if (item.status === 'needs_review') status = 'AWAITING_DECISION';
    else if (item.status === 'cannot_fix') status = 'CANNOT_RESOLVE';
    else if (item.status === 'fixed') status = 'AUTO_FIXED';
    else status = 'FLAGGED';

    const severity: Issue['severity'] = item.severity === 'critical' || item.severity === 'high' ? 'HIGH' : item.severity === 'medium' ? 'MEDIUM' : 'LOW';

    issues.push({
      issue_id: `ISS-${i + 1}`,
      severity,
      type: item.type || 'UNKNOWN',
      column: item.column || 'multiple',
      status,
      linked_question_id: linkedQId,
      summary: item.description || '',
      business_explanation: item.description || 'Data quality issue detected.',
      action_taken: item.status === 'fixed' ? (item.action || 'Corrected automatically') : null,
      action_reason: item.action || null,
      next_action: linkedQId ? `Go to Question ${linkedQId} to resolve.` : null,
      affected_count: item.count || 0,
      affected_rows: [],
      value_distribution: null,
      phase: 'PHASE_2',
      rule_id: item.type || 'auto',
    });
  });

  return issues;
}

function findRelatedIssueCount(issuesFound: any[], sq: any): number {
  const cols = sq.affectedColumns || [];
  let count = 0;
  for (const issue of issuesFound) {
    if (cols.includes(issue.column)) {
      count += issue.count || 0;
    }
  }
  return count;
}

function mapQuestionsFromResult(raw: any): Question[] {
  const questions: Question[] = [];
  const stakeholderQuestions = raw.stakeholderQuestions || [];
  const issuesFound = raw.issuesFound || [];

  stakeholderQuestions.forEach((sq: any, i: number) => {
    const severity: Question['severity'] = sq.priority === 'high' ? 'HIGH' : sq.priority === 'medium' ? 'MEDIUM' : 'LOW';
    const recordsAffected = findRelatedIssueCount(issuesFound, sq);

    const linkedIssueIndex = issuesFound.findIndex((iss: any) =>
      (sq.affectedColumns || []).includes(iss.column) && (iss.status === 'needs_review' || iss.status === 'cannot_fix')
    );
    const linkedIssueId = linkedIssueIndex >= 0 ? `ISS-${linkedIssueIndex + 1}` : null;

    questions.push({
      question_id: `Q-${i + 1}`,
      severity,
      question_text: sq.question || '',
      business_context: sq.context || '',
      columns_affected: sq.affectedColumns || [],
      records_affected: recordsAffected,
      linked_issue_id: linkedIssueId,
      status: 'OPEN',
      answer_type: 'SINGLE_CHOICE',
      options: [
        { option_id: 'ACCEPT', label: 'Accept current state', description: 'Acknowledge this finding and continue with the data as-is', preview_statement: `${recordsAffected} records acknowledged. Issue will be marked as resolved.` },
        { option_id: 'FLAG', label: 'Flag for manual review', description: 'Mark affected records for stakeholder review in the output', preview_statement: `${recordsAffected} records will be flagged. They remain in the dataset but are highlighted for review.` },
        { option_id: 'NOTE', label: 'Add resolution note', description: 'Record a decision note for the permanent audit trail', preview_statement: 'Your note will be attached to this issue in the audit trail.' },
      ],
      selected_option_id: null,
      applied: false,
      applied_at: null,
      applied_by: null,
      unanswered_consequence: sq.consequence || `These ${recordsAffected} records will remain flagged and unresolved in your output.`,
    });
  });

  return questions;
}

function mapQualityScore(raw: any): QualityScore {
  if (raw?.pipeline === 'prose') {
    const beforeRaw = raw.stats?.qualityScoreBefore;
    const afterRaw = raw.stats?.qualityScoreAfter;
    let before = Math.max(0, Math.min(100, beforeRaw != null ? Math.round(beforeRaw) : 0));
    let after = Math.max(0, Math.min(100, afterRaw != null ? Math.round(afterRaw) : before));
    let revisedDown = false;
    if (after < before) { before = after; revisedDown = true; }
    return {
      overall: after,
      completeness: after,
      consistency: after,
      validity: after,
      before,
      after,
      revisedDown,
    };
  }

  const totalRows = raw.stats?.totalRows || raw.stats?.rowsAfterCleaning || 1;
  const nullsFilled = raw.stats?.nullsFilled || 0;
  const dupsRemoved = raw.stats?.duplicatesRemoved || 0;
  const issues = raw.issuesFound || [];
  const issueCount = issues.length;
  const pendingCount = issues.filter((i: any) => i.status === 'needs_review' || i.status === 'cannot_fix').length;

  const derivedBefore = Math.max(40, 100 - Math.min(50, issueCount * 4) - Math.min(10, Math.floor((nullsFilled / Math.max(totalRows, 1)) * 50)));
  const derivedAfter = Math.max(derivedBefore, Math.min(100, 100 - pendingCount * 3));

  const beforeRaw = raw.stats?.qualityScoreBefore;
  const afterRaw = raw.stats?.qualityScoreAfter;
  let before = beforeRaw != null && beforeRaw > 0 ? Math.round(beforeRaw) : derivedBefore;
  let after = afterRaw != null && afterRaw > 0 ? Math.round(afterRaw) : derivedAfter;
  let revisedDown = !!raw.qualityRevisedDown;
  if (after < before) {
    before = after;
    revisedDown = true;
  }

  const completeness = Math.max(0, Math.min(100, Math.round(after + (nullsFilled > 0 ? 2 : 0))));
  const consistency = Math.max(0, Math.min(100, Math.round(after - Math.min(issueCount, 5))));
  const reconRate = Math.max(0, Math.min(100, Math.round(((totalRows - dupsRemoved) / Math.max(totalRows, 1)) * 100)));

  return {
    overall: after,
    completeness,
    consistency,
    validity: reconRate,
    before,
    after,
    revisedDown,
  };
}

function mapPipelinePhasesProse(raw: any): PipelinePhase[] {
  const stats = raw.stats || {};
  const docs = stats.documentsReviewed || (raw.originalFileNames || []).length || 1;
  const sections = stats.sectionsExtracted || 0;
  const claims = stats.quantitativeClaims || 0;
  const disagreements = stats.deterministicDisagreements || 0;
  const questionsCount = stats.questionsRaised || (raw.stakeholderQuestions || []).length || 0;
  const issuesCount = stats.issuesFound || (raw.issuesFound || []).length || 0;

  return [
    {
      phase_number: 1,
      name: 'Document Ingestion',
      sub_label: 'PARSE FILES, EXTRACT TEXT',
      status: 'complete',
      narrative: `Parsed ${docs.toLocaleString()} document${docs === 1 ? '' : 's'} and extracted readable text.`,
      fixed_count: docs, flagged_count: 0, skipped_count: 0,
      group_stats: [{ label: 'documents parsed', count: docs, tone: 'green' }],
    },
    {
      phase_number: 2,
      name: 'Section Extraction',
      sub_label: 'HEADINGS, BODY, STRUCTURE',
      status: 'complete',
      narrative: `Identified ${sections.toLocaleString()} section${sections === 1 ? '' : 's'} across the document${docs === 1 ? '' : 's'}.`,
      fixed_count: sections, flagged_count: 0, skipped_count: 0,
      group_stats: [{ label: 'sections extracted', count: sections, tone: 'green' }],
    },
    {
      phase_number: 3,
      name: 'Quantitative Claim Detection',
      sub_label: 'NUMBERS, DATES, THRESHOLDS',
      status: 'complete',
      narrative: `Detected ${claims.toLocaleString()} quantitative claim${claims === 1 ? '' : 's'} (currencies, percentages, durations, dates).`,
      fixed_count: claims, flagged_count: 0, skipped_count: 0,
      group_stats: [{ label: 'claims captured', count: claims, tone: 'blue' }],
    },
    {
      phase_number: 4,
      name: 'Cross-Document Reconciliation',
      sub_label: 'AGREEMENT, CONFLICTS',
      status: disagreements > 0 ? 'partial' : 'complete',
      narrative: docs > 1
        ? `${disagreements.toLocaleString()} deterministic disagreement${disagreements === 1 ? '' : 's'} found between documents.`
        : `Single document — no cross-document comparison performed.`,
      fixed_count: 0, flagged_count: disagreements, skipped_count: 0,
      pending_count: disagreements,
      group_stats: [{ label: 'disagreements flagged', count: disagreements, tone: disagreements > 0 ? 'amber' : 'green' }],
    },
    {
      phase_number: 5,
      name: 'Stakeholder Question Synthesis',
      sub_label: 'AMBIGUITIES, OWNER REVIEW',
      status: questionsCount > 0 ? 'partial' : 'complete',
      narrative: `Raised ${questionsCount.toLocaleString()} stakeholder question${questionsCount === 1 ? '' : 's'} from ${issuesCount.toLocaleString()} intra-document issue${issuesCount === 1 ? '' : 's'}.`,
      fixed_count: 0, flagged_count: questionsCount, skipped_count: 0,
      pending_count: questionsCount,
      group_stats: [
        { label: 'issues identified', count: issuesCount, tone: 'amber' },
        { label: 'questions raised', count: questionsCount, tone: 'blue' },
      ],
    },
  ];
}

function mapPipelinePhases(raw: any): PipelinePhase[] {
  if (raw?.pipeline === 'prose') return mapPipelinePhasesProse(raw);
  const stats = raw.stats || {};
  const issues = raw.issuesFound || [];
  const questions = raw.stakeholderQuestions || [];

  const totalRows = stats.totalRows || 0;
  const rowsAfter = stats.rowsAfterCleaning || totalRows;
  const columns = stats.columnsProcessed || 0;
  const nullsFilled = stats.nullsFilled || 0;
  const dupsRemoved = stats.duplicatesRemoved || 0;
  const typesFixed = stats.typesFixed || 0;
  const formats = stats.formatsStandardized || 0;
  const fileCount = raw.originalFileNames?.length || 1;

  const fixedIssues = issues.filter((i: any) => i.status === 'fixed');
  const needsReviewIssues = issues.filter((i: any) => i.status === 'needs_review' || i.status === 'cannot_fix');

  const dateIssues = issues.filter((i: any) => /date/i.test(i.column || '') || /date/i.test(i.type || ''));
  const amountIssues = issues.filter((i: any) => /amount|currency|price|cost|number/i.test(i.column || '') || /format|outlier/i.test(i.type || ''));
  const vendorIssues = issues.filter((i: any) => /vendor|entity|name|description/i.test(i.column || ''));
  const duplicateIssues = issues.filter((i: any) => /duplicate/i.test(i.type || ''));
  const nullIssues = issues.filter((i: any) => i.type === 'null' || /null|missing/i.test(i.type || ''));
  const programCodeIssues = issues.filter((i: any) => /program|code|prj/i.test(i.column || ''));

  const phase2FixedCount = formats + typesFixed + dateIssues.filter((i: any) => i.status === 'fixed').length;
  const phase2PendingCount = needsReviewIssues.filter((i: any) => vendorIssues.includes(i) || amountIssues.includes(i) || programCodeIssues.includes(i)).length;

  const phase3FixedCount = dupsRemoved + nullsFilled;
  const phase3PendingCount = duplicateIssues.filter((i: any) => i.status !== 'fixed').length + nullIssues.filter((i: any) => i.status !== 'fixed').length;

  const phase4Fixed = fixedIssues.length;
  const phase4Flagged = needsReviewIssues.length;
  const phase4Open = questions.length;

  const dateFixed = dateIssues.filter((i: any) => i.status === 'fixed').length;
  const datePending = dateIssues.filter((i: any) => i.status !== 'fixed').length;
  const amountFixed = amountIssues.filter((i: any) => i.status === 'fixed').length;
  const amountPending = amountIssues.filter((i: any) => i.status !== 'fixed').length;
  const vendorFixed = vendorIssues.filter((i: any) => i.status === 'fixed').length;
  const vendorPending = vendorIssues.filter((i: any) => i.status !== 'fixed').length;
  const programCodePending = programCodeIssues.filter((i: any) => i.status !== 'fixed').length;

  return [
    {
      phase_number: 1,
      name: 'Data Ingestion',
      sub_label: 'FILE LOADING, ENCODING, STRUCTURE',
      status: 'complete',
      narrative: `Loaded ${totalRows.toLocaleString()} records from ${fileCount} file(s). Detected ${columns} columns. All rows ingested successfully.`,
      fixed_count: totalRows,
      flagged_count: 0,
      skipped_count: 0,
      sub_items: [
        { icon: 'check', label: `${totalRows.toLocaleString()} rows loaded successfully` },
        { icon: 'check', label: `${columns} columns detected and typed` },
        { icon: 'check', label: 'No encoding issues found' },
      ],
      group_stats: [
        { label: 'rows ingested', count: totalRows, tone: 'green' },
        { label: 'columns detected', count: columns, tone: 'green' },
        { label: 'files parsed', count: fileCount, tone: 'green' },
      ],
      detail_cards: [
        {
          title: 'File Loading',
          items: [
            { icon: 'check', label: `${fileCount} file(s) parsed successfully` },
            { icon: 'check', label: `${totalRows.toLocaleString()} rows ingested` },
            { icon: 'check', label: 'Headers detected and validated' },
          ],
        },
        {
          title: 'Encoding & Format',
          items: [
            { icon: 'check', label: 'UTF-8 encoding verified' },
            { icon: 'check', label: 'No structural errors found' },
            { icon: 'check', label: 'All rows parsed cleanly' },
          ],
        },
        {
          title: 'Structure',
          items: [
            { icon: 'check', label: `${columns} columns detected` },
            { icon: 'check', label: 'Column types inferred' },
            { icon: 'check', label: 'No duplicate headers' },
          ],
        },
      ],
    },
    {
      phase_number: 2,
      name: 'Field Normalization',
      sub_label: 'DATES, AMOUNTS, VENDORS, PROGRAM CODES',
      status: phase2PendingCount > 0 ? 'partial' : 'complete',
      narrative: `Field-level values standardized. ${phase2FixedCount} automatic corrections made.${phase2PendingCount > 0 ? ` ${phase2PendingCount} issues flagged for stakeholder decisions.` : ''}`,
      fixed_count: phase2FixedCount,
      flagged_count: phase2PendingCount,
      skipped_count: 0,
      pending_count: phase2PendingCount,
      sub_items: [],
      group_stats: [
        { label: 'values auto-corrected', count: phase2FixedCount, tone: 'green' },
        { label: 'issues need your input', count: phase2PendingCount, tone: 'amber' },
        ...(dateFixed > 0 ? [{ label: 'dates standardized', count: dateFixed, tone: 'green' as const }] : []),
        ...(formats > 0 ? [{ label: 'currency formats parsed', count: formats, tone: 'green' as const }] : []),
      ],
      detail_cards: [
        {
          title: 'Dates',
          items: [
            { icon: 'check', label: `${dateFixed || typesFixed} dates normalized to ISO 8601` },
            { icon: 'check', label: 'FY period assigned to all records' },
            ...(datePending > 0
              ? [{ icon: 'warning' as const, label: `${datePending} ambiguous date formats need review` }]
              : [{ icon: 'check' as const, label: 'No ambiguous date formats found' }]),
          ],
        },
        {
          title: 'Amounts & Currency',
          items: [
            { icon: 'check', label: 'Currency symbols stripped, parsed' },
            ...(formats > 0 ? [{ icon: 'check' as const, label: `${formats} number formats standardized` }] : []),
            ...(amountPending > 0
              ? [{ icon: 'warning' as const, label: `${amountPending} records missing amount \u2014 pending review` }]
              : [{ icon: 'check' as const, label: 'All amounts parsed successfully' }]),
          ],
        },
        {
          title: 'Vendors & Codes',
          items: [
            { icon: 'check', label: 'Vendor names trimmed, casing normalized' },
            ...(vendorFixed > 0 ? [{ icon: 'check' as const, label: `${vendorFixed} vendor names standardized` }] : []),
            ...(vendorPending > 0
              ? [{ icon: 'warning' as const, label: `${vendorPending} records: vendor variant \u2014 pending review` }]
              : [{ icon: 'check' as const, label: 'No vendor variants found' }]),
            ...(programCodePending > 0
              ? [{ icon: 'warning' as const, label: `${programCodePending} records: program code missing` }]
              : []),
          ],
        },
      ],
    },
    {
      phase_number: 3,
      name: 'Deduplication & Null Handling',
      sub_label: 'DUPLICATE DETECTION, NULL IMPUTATION',
      status: phase3PendingCount > 0 ? 'partial' : 'complete',
      narrative: `${dupsRemoved} duplicate records removed. ${nullsFilled} null values filled.${phase3PendingCount > 0 ? ` ${phase3PendingCount} flagged for review.` : ''}`,
      fixed_count: phase3FixedCount,
      flagged_count: phase3PendingCount,
      skipped_count: 0,
      pending_count: phase3PendingCount,
      sub_items: [],
      group_stats: [
        { label: 'duplicates removed', count: dupsRemoved, tone: 'green' },
        { label: 'nulls filled', count: nullsFilled, tone: 'green' },
        { label: 'pending review', count: phase3PendingCount, tone: 'amber' },
      ],
      detail_cards: [
        {
          title: 'Duplicate Detection',
          items: [
            ...(dupsRemoved > 0
              ? [{ icon: 'check' as const, label: `${dupsRemoved} exact duplicate rows removed` }]
              : [{ icon: 'info' as const, label: 'No exact duplicates found' }]),
            { icon: 'check', label: 'Primary key uniqueness verified' },
            ...(duplicateIssues.filter((i: any) => i.status !== 'fixed').length > 0
              ? [{ icon: 'warning' as const, label: `${duplicateIssues.filter((i: any) => i.status !== 'fixed').length} potential fuzzy duplicates flagged` }]
              : [{ icon: 'check' as const, label: 'No fuzzy duplicates detected' }]),
          ],
        },
        {
          title: 'Null Handling',
          items: [
            ...(nullsFilled > 0
              ? [{ icon: 'check' as const, label: `${nullsFilled} nulls imputed (median/mode)` }]
              : [{ icon: 'info' as const, label: 'No nulls required imputation' }]),
            ...(nullIssues.filter((i: any) => i.status !== 'fixed').length > 0
              ? [{ icon: 'warning' as const, label: `${nullIssues.filter((i: any) => i.status !== 'fixed').length} critical nulls need review` }]
              : [{ icon: 'check' as const, label: 'No critical nulls remaining' }]),
            { icon: 'check', label: 'Null handling policy applied' },
          ],
        },
        {
          title: 'Data Quality',
          items: [
            { icon: 'check', label: `${rowsAfter.toLocaleString()} clean records retained` },
            { icon: 'check', label: 'Referential integrity checked' },
            { icon: 'check', label: 'Row counts verified' },
          ],
        },
      ],
    },
    {
      phase_number: 4,
      name: 'Anomaly Detection',
      sub_label: 'OUTLIERS, INCONSISTENCIES, BUSINESS RULES',
      status: phase4Open > 0 ? 'partial' : 'complete',
      narrative: `${issues.length} issues identified across ${rowsAfter.toLocaleString()} records. ${phase4Fixed} auto-resolved, ${phase4Flagged} need stakeholder decisions.`,
      fixed_count: phase4Fixed,
      flagged_count: phase4Flagged,
      skipped_count: 0,
      pending_count: phase4Open,
      sub_items: [],
      group_stats: [
        { label: 'issues fixed', count: phase4Fixed, tone: 'green' },
        { label: 'flagged for review', count: phase4Flagged, tone: 'amber' },
        { label: 'questions generated', count: phase4Open, tone: 'blue' },
      ],
      detail_cards: [
        {
          title: 'Auto-Resolved',
          items: [
            { icon: 'check', label: `${phase4Fixed} issues fixed automatically` },
            { icon: 'check', label: 'Logged to audit trail' },
            { icon: 'check', label: 'Original values preserved' },
          ],
        },
        {
          title: 'Flagged for Review',
          items: [
            ...(phase4Flagged > 0
              ? [{ icon: 'warning' as const, label: `${phase4Flagged} issues awaiting stakeholder decision` }]
              : [{ icon: 'check' as const, label: 'All anomalies auto-resolved' }]),
            ...(phase4Open > 0
              ? [{ icon: 'warning' as const, label: `${phase4Open} questions generated` }]
              : [{ icon: 'check' as const, label: 'No stakeholder input needed' }]),
            { icon: 'info', label: 'See Questions tab for details' },
          ],
        },
        {
          title: 'Business Rules',
          items: [
            { icon: 'check', label: 'Validation rules applied' },
            { icon: 'check', label: 'Outlier detection complete' },
            { icon: 'check', label: 'Consistency checks passed' },
          ],
        },
      ],
    },
    {
      phase_number: 5,
      name: 'Output Assembly',
      sub_label: 'MASTER DATASET, EXPORTS, AUDIT TRAIL',
      status: 'complete',
      narrative: `Final dataset: ${rowsAfter.toLocaleString()} rows across ${columns} columns. Audit trail written for all transformations.`,
      fixed_count: rowsAfter,
      flagged_count: 0,
      skipped_count: 0,
      sub_items: [],
      group_stats: [
        { label: 'rows in output', count: rowsAfter, tone: 'green' },
        { label: 'columns preserved', count: columns, tone: 'green' },
        { label: 'transformations logged', count: phase2FixedCount + phase3FixedCount + phase4Fixed, tone: 'blue' },
      ],
      detail_cards: [
        {
          title: 'Master Dataset',
          items: [
            { icon: 'check', label: `${rowsAfter.toLocaleString()} rows in final output` },
            { icon: 'check', label: `${columns} columns preserved` },
            { icon: 'check', label: 'Schema consistent across all rows' },
          ],
        },
        {
          title: 'Exports',
          items: [
            { icon: 'check', label: 'CSV export ready' },
            { icon: 'check', label: 'Excel-compatible formatting' },
            { icon: 'check', label: 'UTF-8 encoding' },
          ],
        },
        {
          title: 'Audit Trail',
          items: [
            { icon: 'check', label: `${phase2FixedCount + phase3FixedCount + phase4Fixed} transformations logged` },
            { icon: 'check', label: 'Before/after values preserved' },
            { icon: 'check', label: 'Full replay capability' },
          ],
        },
      ],
    },
  ];
}

function mapChangeLog(raw: any, appliedAuditEntries: any[] = [], filterUnitId: string | null = null): ChangeLogEntry[] {
  const entries: ChangeLogEntry[] = [];
  const changeLog = raw.changeLog || [];

  changeLog.forEach((cl: any, i: number) => {
    entries.push({
      id: `CL-${i + 1}`,
      category: 'auto_fixed',
      title: cl.operation || 'Data Transformation',
      description: cl.details || '',
      phase: 'Phase 2',
      field: 'multiple',
      action_type: 'auto_fixed',
      count: cl.rowsAffected || 0,
      details: [],
    });
  });

  const grouped = new Map<string, { entry: ChangeLogEntry; rowSet: Set<number> }>();
  for (const audit of appliedAuditEntries) {
    if (!audit) continue;
    if (filterUnitId && audit.unit_id && audit.unit_id !== filterUnitId) continue;
    const rule = audit.rule || 'auto_fix';
    const action = audit.action || 'UPDATED';
    const isUserDecision = audit.action === 'ACKNOWLEDGED' || audit.action === 'NOTE_ADDED' || audit.action === 'FLAGGED_FOR_REVIEW';
    const groupKey = `${rule}::${action}`;

    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, {
        entry: {
          id: `CL-AUDIT-${groupKey}`,
          category: isUserDecision ? 'user_decision' : 'auto_fixed',
          title: humaniseAuditRule(rule, action),
          description: audit.detail || '',
          phase: audit.phase || 'PHASE_6',
          field: audit.field || 'multiple',
          action_type: isUserDecision ? 'user_decision' : 'auto_fixed',
          count: 0,
          details: [],
        },
        rowSet: new Set<number>(),
      });
    }

    const bucket = grouped.get(groupKey)!;
    if (audit.row_id != null) {
      bucket.rowSet.add(Number(audit.row_id));
      bucket.entry.details.push({
        row: Number(audit.row_id),
        field: audit.field || '',
        original_value: audit.old_value != null ? String(audit.old_value) : '',
        current_value: audit.new_value != null ? String(audit.new_value) : '',
        rule,
      });
    } else if (audit.rows_affected) {
      bucket.entry.count = Math.max(bucket.entry.count, Number(audit.rows_affected) || 0);
    }
    if (audit.detail && !bucket.entry.description) {
      bucket.entry.description = audit.detail;
    }
  }

  for (const { entry, rowSet } of grouped.values()) {
    if (rowSet.size > 0) entry.count = rowSet.size;
    entries.push(entry);
  }

  return entries;
}

function humaniseAuditRule(rule: string, action: string): string {
  const map: Record<string, string> = {
    auto_fix_trim_whitespace: 'Whitespace trimmed',
    auto_fix_standardize_case: 'Case standardized',
    auto_fix_fill_missing_constant: 'Missing values filled (constant)',
    auto_fix_fill_missing_statistical: 'Missing values filled (statistical)',
    auto_fix_remove_duplicates: 'Duplicate rows removed',
    auto_fix_standardize_format: 'Format standardized',
    auto_fix_replace_value: 'Find & replace applied',
    auto_fix_apply_mapping: 'Canonical mapping applied',
    stakeholder_acknowledgement: 'Question acknowledged',
    manual_review_flag: 'Flagged for manual review',
    resolution_note: 'Resolution note added',
  };
  if (map[rule]) return map[rule];
  if (action === 'NO_CHANGE') return 'No-op (rule made no changes)';
  if (action === 'VALIDATION_ERROR') return 'Validation error';
  return rule.replace(/^auto_fix_/, '').replace(/_/g, ' ');
}

type TabId = 'overview' | 'issues' | 'questions' | 'changelog' | 'preview' | 'crossunit';

function unitOrLegacy(v2: ReturnType<typeof adaptToCleaningResultsV2>, unitId: string | null): any {
  const unit = unitId
    ? (v2.units.find(u => u.unit_id === unitId) || v2.units[0])
    : v2.units[0];
  if (!unit) {
    return {
      pipeline: v2.pipeline || 'tabular',
      issuesFound: [], stakeholderQuestions: [], stats: {}, changeLog: [],
      previewBefore: '', previewAfter: '', cleaningLog: '', originalFileNames: v2.originalFileNames,
    };
  }
  return {
    pipeline: v2.pipeline || 'tabular',
    issuesFound: unit.issuesFound || [],
    stakeholderQuestions: unit.stakeholderQuestions || [],
    canonicalMapping: unit.canonicalMapping || [],
    stats: {
      ...(unit.stats || {}),
      qualityScoreBefore: unit.qualityScoreBefore,
      qualityScoreAfter: unit.qualityScoreAfter,
      totalRows: unit.rows_total,
      rowsAfterCleaning: unit.rows_after,
      columnsProcessed: unit.columns_processed,
    },
    cleaningLog: unit.cleaningLog || v2.cleaningLog || '',
    previewBefore: unit.previewBefore || '',
    previewAfter: unit.previewAfter || '',
    changeLog: (v2.changeLog || []).filter((cl: any) => !cl?.unit_id || cl.unit_id === unit.unit_id),
    originalFileNames: v2.originalFileNames || [unit.file_name],
    sessionId: v2.sessionId,
    qualityRevisedDown: !!unit.qualityRevisedDown,
  };
}

const CleaningResultsPanel: React.FC<CleaningResultsPanelProps> = ({
  result, isExpanded, onToggleExpand, onSendToSmartAnalyst, onSendToCouncil,
}) => {
  const v2Result = useMemo(() => adaptToCleaningResultsV2(result), [result]);
  const isMultiUnit = v2Result.units.length > 1;

  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [activeUnitId, setActiveUnitId] = useState<string | null>(() => v2Result.units[0]?.unit_id || null);
  const [allQuestions, setAllQuestions] = useState<Question[]>([]);
  const [issueOverrides, setIssueOverrides] = useState<Record<string, Partial<Issue>>>({});
  const [qualityOverrides, setQualityOverrides] = useState<Record<string, Partial<QualityScore>>>({});
  const [highlightQuestionId, setHighlightQuestionId] = useState<string | null>(null);
  const [highlightIssueId, setHighlightIssueId] = useState<string | null>(null);
  const [appliedAudit, setAppliedAudit] = useState<any[]>([]);
  const [livePreviewRows, setLivePreviewRows] = useState<DataPreviewRow[]>([]);
  const [proseDocuments, setProseDocuments] = useState<Array<{ filename: string; sections: Array<{ section_id: string; heading: string; body: string }> }> | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [availableColumns, setAvailableColumns] = useState<string[]>(() => {
    const unit = v2Result.units[0];
    return (unit?.stats?.columnNames as string[]) || result?.stats?.columnNames || [];
  });

  const activeUnit: SheetUnit | null = useMemo(() => {
    if (!activeUnitId) return v2Result.units[0] || null;
    return v2Result.units.find(u => u.unit_id === activeUnitId) || v2Result.units[0] || null;
  }, [v2Result, activeUnitId]);

  useEffect(() => {
    setActiveUnitId(v2Result.units[0]?.unit_id || null);
    setQualityOverrides({});
    setIssueOverrides({});
  }, [result?.sessionId]);

  useEffect(() => {
    if (activeUnitId === RECON_PILL_ID) return;
    if (activeUnitId && !v2Result.units.some(u => u.unit_id === activeUnitId)) {
      setActiveUnitId(v2Result.units[0]?.unit_id || null);
    }
  }, [v2Result, activeUnitId]);

  const unitView = useMemo(() => unitOrLegacy(v2Result, activeUnitId), [v2Result, activeUnitId]);

  const issues = useMemo<Issue[]>(() => {
    const base = mapIssuesFromResult(unitView);
    if (Object.keys(issueOverrides).length === 0) return base;
    return base.map(iss => {
      const key = `${activeUnitId || ''}::${iss.issue_id}`;
      const patch = issueOverrides[key];
      return patch ? { ...iss, ...patch } : iss;
    });
  }, [unitView, issueOverrides, activeUnitId]);

  const questions = useMemo<Question[]>(() => {
    if (activeUnitId === RECON_PILL_ID) {
      return allQuestions.filter(q => q.question_type === 'Q_CROSS_UNIT_RECONCILIATION');
    }
    if (allQuestions.length === 0) {
      return mapQuestionsFromResult(unitView);
    }
    const nonCrossUnit = allQuestions.filter(q => q.question_type !== 'Q_CROSS_UNIT_RECONCILIATION');
    if (!isMultiUnit) return nonCrossUnit;
    return nonCrossUnit.filter(q => !q.unit_id || q.unit_id === activeUnitId);
  }, [allQuestions, activeUnitId, isMultiUnit, unitView]);

  const qualityScore = useMemo<QualityScore>(() => {
    const isProseHere = result?.pipeline === 'prose';
    const viewWithPipeline = isProseHere ? { ...unitView, pipeline: 'prose' } : unitView;
    const base = mapQualityScore(viewWithPipeline);
    const override = activeUnitId ? qualityOverrides[activeUnitId] : undefined;
    return override ? { ...base, ...override } : base;
  }, [unitView, qualityOverrides, activeUnitId, result]);

  useEffect(() => {
    if (Array.isArray(unitView?.stats?.columnNames) && unitView.stats.columnNames.length > 0) {
      setAvailableColumns(prev => {
        const next = unitView.stats.columnNames as string[];
        if (prev.length === next.length && prev.every((c, i) => c === next[i])) return prev;
        return next;
      });
    }
  }, [unitView]);

  const pipelinePhases = useMemo(() => {
    const isProseHere = result?.pipeline === 'prose';
    const viewWithPipeline = isProseHere ? { ...unitView, pipeline: 'prose' } : unitView;
    return mapPipelinePhases(viewWithPipeline);
  }, [unitView, result]);
  const changeLog = useMemo(() => mapChangeLog(unitView, appliedAudit, activeUnitId), [unitView, appliedAudit, activeUnitId]);
  const previewRows: DataPreviewRow[] = livePreviewRows;

  const sessionIdForEffect = result.sessionId;
  useEffect(() => {
    if (!sessionIdForEffect) return;

    let cancelled = false;
    const engagementId = String(sessionIdForEffect);
    const retryTimers: number[] = [];

    const buildQuestion = (dbQ: any, currentIssues: Issue[]): Question => {
      const qJson = dbQ.question_json || {};
      const opts = qJson.options || [];
      return {
        question_id: dbQ.id,
        severity: (dbQ.severity || qJson.severity || 'MEDIUM') as Question['severity'],
        question_text: qJson.title || qJson.question_text || '',
        business_context: qJson.description || qJson.business_context || '',
        columns_affected: qJson.affected_columns || qJson.columns_affected || [],
        available_columns: qJson.available_columns || [],
        records_affected: dbQ.affected_row_count || 0,
        linked_issue_id: currentIssues.find(iss =>
          (qJson.affected_columns || []).some((col: string) => col === iss.column) &&
          (iss.status === 'AWAITING_DECISION' || iss.status === 'CANNOT_RESOLVE')
        )?.issue_id || null,
        status: (dbQ.status === 'APPLIED' ? 'APPLIED' : dbQ.status === 'SKIPPED' ? 'APPLIED' : 'OPEN') as Question['status'],
        answer_type: (qJson.answer_type || 'SINGLE_CHOICE') as Question['answer_type'],
        options: opts.map((o: any) => ({
          option_id: o.option_id,
          label: o.label,
          description: o.description || '',
          preview_statement: o.preview_statement || '',
          requires_sub_inputs: o.requires_sub_inputs || false,
          sub_inputs: o.sub_inputs || undefined,
          default_params: o.default_params || undefined,
        })),
        selected_option_id: dbQ.selected_option_id || null,
        applied: dbQ.status === 'APPLIED',
        applied_at: dbQ.applied_at || null,
        applied_by: null,
        unanswered_consequence: qJson.unanswered_consequence || 'These records will remain flagged and unresolved in your output.',
        unit_id: qJson.unit_id || null,
        sheet_name: qJson.sheet_name || null,
        file_name: qJson.file_name || null,
        question_type: dbQ.question_type || qJson.question_type || undefined,
        finding: qJson.finding || undefined,
        finding_kind: qJson.finding_kind || undefined,
        section_refs: qJson.section_refs || qJson.finding?.section_refs || [],
        files: qJson.files || qJson.finding?.files || [],
        claims: qJson.claims || qJson.finding?.claims || [],
        candidate_resolutions: qJson.candidate_resolutions || qJson.finding?.candidate_resolutions || [],
        issue_type: qJson.issue_type || qJson.finding?.issue_type || null,
      };
    };

    const fetchQuestions = async (): Promise<number> => {
      try {
        const res = await api.get('/api/intelligence/pipeline/questions', {
          params: { engagement_id: engagementId, session_id: sessionIdForEffect },
        });
        if (cancelled) return -1;
        if (!res.data?.success || !Array.isArray(res.data.data)) return -1;

        const dbRows = res.data.data;
        if (dbRows.length === 0) return 0;

        const dbQuestions: Question[] = dbRows.map((dbQ: any) => buildQuestion(dbQ, []));
        if (cancelled) return dbQuestions.length;

        setAllQuestions(dbQuestions);

        setIssueOverrides(prev => {
          const next: Record<string, Partial<Issue>> = { ...prev };
          for (const unit of (v2Result.units || [])) {
            const unitIssues = mapIssuesFromResult(unitOrLegacy(v2Result, unit.unit_id));
            for (const iss of unitIssues) {
              if (iss.status !== 'AWAITING_DECISION' && iss.status !== 'CANNOT_RESOLVE') continue;
              const match = dbQuestions.find(q =>
                (!q.unit_id || q.unit_id === unit.unit_id) && (
                  q.linked_issue_id === iss.issue_id ||
                  (q.columns_affected || []).includes(iss.column)
                )
              );
              if (match) {
                const key = `${unit.unit_id}::${iss.issue_id}`;
                next[key] = { ...(next[key] || {}), linked_question_id: match.question_id };
              }
            }
          }
          return next;
        });

        setAvailableColumns(prev => {
          const merged = new Set<string>(prev);
          for (const q of dbQuestions) {
            (q.available_columns || []).forEach(c => merged.add(c));
            (q.columns_affected || []).forEach(c => merged.add(c));
          }
          return merged.size > prev.length ? Array.from(merged) : prev;
        });

        return dbQuestions.length;
      } catch {
        return -1;
      }
    };

    const scheduleRetries = () => {
      const delays = [1500, 4000, 9000];
      for (const ms of delays) {
        const t = window.setTimeout(async () => {
          if (cancelled) return;
          const count = await fetchQuestions();
          if (count > 0) {
            for (const id of retryTimers) clearTimeout(id);
          }
        }, ms);
        retryTimers.push(t);
      }
    };

    fetchQuestions().then((count) => {
      if (count <= 0 && !cancelled) scheduleRetries();
    });

    return () => {
      cancelled = true;
      for (const t of retryTimers) clearTimeout(t);
    };
  }, [sessionIdForEffect]);

  useEffect(() => {
    if (!sessionIdForEffect) return;
    let cancelled = false;

    const fetchSessionPreview = async () => {
      try {
        const params: Record<string, any> = {};
        if (activeUnitId) params.unit_id = activeUnitId;
        const res = await api.get(`/api/intelligence/pipeline/sessions/${sessionIdForEffect}/preview`, { params });
        if (cancelled || !res.data?.success) return;
        const data = res.data.data || {};

        if (Array.isArray(data.preview_rows)) {
          setLivePreviewRows(data.preview_rows);
        }

        if (Array.isArray(data.prose_documents)) {
          setProseDocuments(data.prose_documents);
        } else if (data.pipeline === 'tabular') {
          setProseDocuments(null);
        }

        if (Array.isArray(data.audit)) {
          setAppliedAudit(data.audit);
        }

        if (Array.isArray(data.columns) && data.columns.length > 0) {
          setAvailableColumns(prev => {
            const merged = new Set<string>(prev);
            for (const c of data.columns) merged.add(c);
            return merged.size > prev.length ? Array.from(merged) : prev;
          });
        }
      } catch {
        // silent — preview is enrichment, not critical to first paint
      }
    };

    fetchSessionPreview();
    return () => {
      cancelled = true;
    };
  }, [sessionIdForEffect, activeUnitId]);

  const openIssueCount = issues.filter(i => i.status === 'AWAITING_DECISION' || i.status === 'CANNOT_RESOLVE').length;

  const PROSE_RENDERABLE_EXTS = ['.docx', '.doc', '.md', '.markdown', '.txt', '.pdf'];

  const proseExt = useMemo<string | null>(() => {
    if (result?.pipeline !== 'prose') return null;
    const first = (result?.originalFileNames?.[0] || '').toLowerCase();
    const m = first.match(/\.[a-z0-9]+$/);
    return m ? m[0] : null;
  }, [result]);

  const downloadFormat: 'xlsx' | 'csv' | 'docx' | 'doc' | 'pdf' | 'md' | 'txt' | 'json' = useMemo(() => {
    if (result?.pipeline === 'prose') {
      if (proseExt && PROSE_RENDERABLE_EXTS.includes(proseExt)) {
        const ext = proseExt.replace('.', '') as 'docx' | 'doc' | 'pdf' | 'md' | 'txt' | 'markdown';
        return ext === 'markdown' ? 'md' : ext;
      }
      return 'json';
    }
    const anyExcel = v2Result.units.some(u => /\.xlsx?$/i.test(u.file_name || ''));
    if (anyExcel || v2Result.units.length > 1) return 'xlsx';
    return 'csv';
  }, [result, v2Result, proseExt]);

  const handleDownload = useCallback(async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    const sessionId = v2Result.sessionId || result?.sessionId || null;
    const isProse = result?.pipeline === 'prose';
    // When the user merged files (Union / Match-on-key) the unified result IS
    // the output they want — serve that single file instead of the per-sheet
    // workbook. This also sidesteps the multi-sheet assembly path entirely.
    const combined = result?.combinedFile;
    const combineOk = !!(combined && combined.url && !combined.error);
    try {
      let blobUrl: string | null = null;
      let downloadName = result?.cleanedFileName || 'cleaned_data';
      let mimeType = 'application/octet-stream';

      if (combineOk) {
        const useXlsx = !!combined.xlsxUrl;
        const sourceUrl = combined.xlsxUrl || combined.url;
        const resp = await api.get(sourceUrl, { responseType: 'blob' });
        const modeLabel = combined.mode === 'join' ? 'joined' : 'combined';
        downloadName = useXlsx
          ? (combined.xlsxName || `${modeLabel}_data.xlsx`)
          : (combined.name || `${modeLabel}_data.csv`);
        mimeType = useXlsx
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv';
        blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: mimeType }));
      } else if (isProse && proseExt && PROSE_RENDERABLE_EXTS.includes(proseExt) && sessionId) {
        const sourceFile = result?.originalFileNames?.[0];
        const params: Record<string, any> = {};
        if (sourceFile) params.file = sourceFile;
        const resp = await api.get(`/api/intelligence/sessions/${sessionId}/document`, { params, responseType: 'blob' });
        const sourceBase = ((sourceFile || 'cleaned').replace(/\.[^.]+$/, '')).replace(/[^a-zA-Z0-9._-]/g, '_');
        downloadName = `cleaned_${sourceBase}${proseExt}`;
        const proseMime: Record<string, string> = {
          '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          '.doc': 'application/msword',
          '.pdf': 'application/pdf',
          '.md': 'text/markdown',
          '.markdown': 'text/markdown',
          '.txt': 'text/plain',
        };
        mimeType = proseMime[proseExt] || 'application/octet-stream';
        blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: mimeType }));
      } else if (downloadFormat === 'xlsx' && sessionId) {
        const resp = await api.get(`/api/intelligence/sessions/${sessionId}/workbook`, { responseType: 'blob' });
        const sourceBase = (v2Result.originalFileNames[0] || 'cleaned')
          .replace(/\.[^.]+$/, '')
          .replace(/[^a-zA-Z0-9._-]/g, '_');
        downloadName = `cleaned_${sourceBase}.xlsx`;
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: mimeType }));
      } else if (downloadFormat === 'csv') {
        const unitForDownload = activeUnit || v2Result.units[0];
        const sourceUrl = unitForDownload?.cleanedFileUrl
          || result.cleanedFileUrl
          || (unitForDownload?.cleanedFilePath ? `/api/intelligence/download-pipeline-file?path=${encodeURIComponent(unitForDownload.cleanedFilePath)}` : null)
          || (result.cleanedFilePath ? `/api/intelligence/download-pipeline-file?path=${encodeURIComponent(result.cleanedFilePath)}` : null);
        if (!sourceUrl) {
          toast.error('Cleaned file not available');
          return;
        }
        const resp = await api.get(sourceUrl, { responseType: 'blob' });
        const sourceBase = ((unitForDownload?.file_name || result?.originalFileNames?.[0] || 'cleaned_data')
          .replace(/\.[^.]+$/, '')).replace(/[^a-zA-Z0-9._-]/g, '_');
        downloadName = `cleaned_${sourceBase}.csv`;
        mimeType = 'text/csv';
        blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: mimeType }));
      } else {
        const sourceUrl = result?.cleanedFileUrl
          || (result?.cleanedFilePath ? `/api/intelligence/download-pipeline-file?path=${encodeURIComponent(result.cleanedFilePath)}` : null);
        if (!sourceUrl) {
          toast.error('Reconciled document state is not available for download.');
          return;
        }
        const resp = await api.get(sourceUrl, { responseType: 'blob' });
        const sourceBase = ((result?.originalFileNames?.[0] || 'reconciled')
          .replace(/\.[^.]+$/, '')).replace(/[^a-zA-Z0-9._-]/g, '_');
        downloadName = `${sourceBase}_reconciled_state.json`;
        mimeType = 'application/json';
        blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: mimeType }));
        toast(`Original format (${proseExt || 'unknown'}) is not renderable; downloading reconciled state instead.`, { icon: 'ℹ️', duration: 5000 });
      }

      if (!blobUrl) return;
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Downloaded ${downloadName}`);
    } catch (err: any) {
      const status = err?.response?.status;
      let serverMessage = '';
      const errBody = err?.response?.data;
      if (errBody instanceof Blob) {
        try {
          const text = await errBody.text();
          try {
            const parsed = JSON.parse(text);
            serverMessage = parsed?.error || parsed?.message || text;
          } catch {
            serverMessage = text;
          }
        } catch {
          serverMessage = '';
        }
      } else if (errBody && typeof errBody === 'object') {
        serverMessage = errBody.error || errBody.message || '';
      }
      const msg = serverMessage || err?.message || 'Download failed';
      if (status === 404) {
        toast.error(`Cleaned file not found: ${msg}`);
      } else if (status === 415) {
        toast.error(`Original format not supported for re-export: ${msg}`);
      } else {
        toast.error(`Download failed (${status || 'no status'}): ${msg}`);
      }
    } finally {
      setIsDownloading(false);
    }
  }, [result, activeUnit, v2Result, downloadFormat, isDownloading, proseExt]);

  const navigateToQuestion = useCallback((questionId: string) => {
    setActiveTab('questions');
    setHighlightQuestionId(questionId);
    setTimeout(() => setHighlightQuestionId(null), 2000);
  }, []);

  const navigateToIssue = useCallback((issueId: string) => {
    setActiveTab('issues');
    setHighlightIssueId(issueId);
    setTimeout(() => setHighlightIssueId(null), 2000);
  }, []);

  const handleApplyQuestion = useCallback(async (questionId: string, optionId: string, params?: Record<string, any>) => {
    const targetQuestion = questions.find(q => q.question_id === questionId);
    const sanitizedActiveUnit = activeUnitId === RECON_PILL_ID ? null : activeUnitId;
    const targetUnitId = targetQuestion?.unit_id || sanitizedActiveUnit || null;
    const targetUnit = targetUnitId ? v2Result.units.find(u => u.unit_id === targetUnitId) : (v2Result.units[0] || null);
    const dfPath = targetUnit?.cleanedFilePath || result.cleanedFilePath || '';
    const engagementId = v2Result.sessionId ? String(v2Result.sessionId) : (result.sessionId ? String(result.sessionId) : '');

    try {
      const response = await api.post(`/api/intelligence/pipeline/questions/${questionId}/apply`, {
        option_id: optionId,
        answer_params: params || {},
        dfPath,
        engagement_id: engagementId,
        unit_id: targetUnitId,
      });

      if (response.data.success) {
        setAllQuestions(prev => prev.map(q => {
          if (q.question_id === questionId) {
            return { ...q, status: 'APPLIED' as const, applied: true, selected_option_id: optionId, applied_at: new Date().toISOString(), applied_by: 'Current User' };
          }
          return q;
        }));

        const linkedIssueId = questions.find(q => q.question_id === questionId)?.linked_issue_id;
        if (linkedIssueId && targetUnitId) {
          const overrideKey = `${targetUnitId}::${linkedIssueId}`;
          setIssueOverrides(prev => ({
            ...prev,
            [overrideKey]: {
              ...(prev[overrideKey] || {}),
              status: 'AUTO_FIXED',
              action_taken: `Resolved via stakeholder decision (${optionId})`,
            },
          }));
        }

        const data = response.data.data || {};
        if (Array.isArray(data.audit) && data.audit.length > 0) {
          setAppliedAudit(prev => [...prev, ...data.audit]);
        }
        if (Array.isArray(data.preview_rows) && data.preview_rows.length > 0) {
          setLivePreviewRows(data.preview_rows);
        }
        if (Array.isArray(data.columns) && data.columns.length > 0) {
          setAvailableColumns(prev => {
            const merged = new Set([...prev, ...data.columns]);
            return Array.from(merged);
          });
        }

        if (data.quality_score && targetUnitId) {
          const qs = data.quality_score;
          const apiAfter = (qs.qualityScoreAfter != null) ? Number(qs.qualityScoreAfter) : (qs.overall_score != null ? Number(qs.overall_score) * 100 : null);
          const apiBefore = (qs.qualityScoreBefore != null) ? Number(qs.qualityScoreBefore) : null;
          setQualityOverrides(prev => {
            const prevForUnit = prev[targetUnitId] || {};
            const fallbackAfter = prevForUnit.after ?? qualityScore.after;
            const fallbackBefore = prevForUnit.before ?? qualityScore.before;
            const fallbackCompleteness = prevForUnit.completeness ?? qualityScore.completeness;
            const fallbackConsistency = prevForUnit.consistency ?? qualityScore.consistency;
            const fallbackOverall = prevForUnit.overall ?? qualityScore.overall;
            const nextAfter = apiAfter != null ? Math.round(apiAfter) : fallbackAfter;
            const nextBefore = apiBefore != null ? Math.round(apiBefore) : fallbackBefore;
            const nextCompleteness = qs.completeness != null ? Math.round(Number(qs.completeness) * 100) : fallbackCompleteness;
            const nextConsistency = qs.consistency != null ? Math.round(Number(qs.consistency) * 100) : fallbackConsistency;
            const nextOverall = qs.overall_score != null ? Math.round(Number(qs.overall_score) * 100) : (apiAfter != null ? Math.round(apiAfter) : fallbackOverall);
            return {
              ...prev,
              [targetUnitId]: {
                ...prevForUnit,
                overall: nextOverall,
                completeness: nextCompleteness,
                consistency: nextConsistency,
                before: nextBefore,
                after: nextAfter,
              },
            };
          });
        }

        const summary = data.audit_summary;
        const isProseApply = data.pipeline === 'prose' || optionId.startsWith('PROSE_FIX_');
        const noun = isProseApply ? 'section' : 'row';
        if (summary && summary.summaryAction === 'VALIDATION_ERROR') {
          toast.error('Auto-fix could not be applied \u2014 adjust the rule inputs and try again.');
        } else if (summary && summary.rowsChanged > 0) {
          toast.success(`Applied \u2014 ${summary.rowsChanged} ${noun}${summary.rowsChanged === 1 ? '' : 's'} changed.`);
        } else {
          toast.success('Decision applied successfully');
        }
      } else {
        throw new Error(response.data.error || 'Apply failed');
      }
    } catch (err: any) {
      const errMsg = err.response?.data?.error || err.message || '';
      if (errMsg.includes('not found')) {
        const sessionEngagementId = result.sessionId ? String(result.sessionId) : '';
        try {
          const refreshRes = await api.get('/api/intelligence/pipeline/questions', { params: { engagement_id: sessionEngagementId, session_id: result.sessionId } });
          if (refreshRes.data.success && refreshRes.data.data?.length > 0) {
            toast.error('Questions were refreshed. Please try applying again.');
            return;
          }
        } catch {}
      }
      const msg = errMsg || 'Something went wrong \u2014 your data was not changed. Try again or contact support.';
      toast.error(msg);
      throw new Error(msg);
    }
  }, [result, questions, activeUnitId, v2Result, qualityScore]);

  const handlePreviewQuestion = useCallback(async (questionId: string, optionId: string, params: Record<string, any>) => {
    const targetQuestion = questions.find(q => q.question_id === questionId);
    const sanitizedActiveUnit = activeUnitId === RECON_PILL_ID ? null : activeUnitId;
    const targetUnitId = targetQuestion?.unit_id || sanitizedActiveUnit || null;
    const targetUnit = targetUnitId ? v2Result.units.find(u => u.unit_id === targetUnitId) : (v2Result.units[0] || null);
    const dfPath = targetUnit?.cleanedFilePath || result.cleanedFilePath || '';
    try {
      const response = await api.post(`/api/intelligence/pipeline/questions/${questionId}/preview-fix`, {
        option_id: optionId,
        answer_params: params,
        dfPath,
        unit_id: targetUnitId,
      });
      if (response.data.success) {
        const data = response.data.data || {};
        if (Array.isArray(data.preview_rows) && data.preview_rows.length > 0) {
          setLivePreviewRows(data.preview_rows);
        }
        const summary = data.audit_summary || { rowsChanged: 0, total: 0, summaryAction: null };
        return {
          rowsChanged: summary.rowsChanged || 0,
          total: summary.total || 0,
          summaryAction: summary.summaryAction || null,
        };
      }
      throw new Error(response.data.error || 'Preview failed');
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Preview failed';
      throw new Error(msg);
    }
  }, [result, questions, activeUnitId, v2Result, qualityScore]);

  const crossUnitFindings = v2Result.crossUnitFindings || null;
  const hasCrossUnitFindings = !!crossUnitFindings && (
    (crossUnitFindings.orphans?.length || 0) > 0 ||
    (crossUnitFindings.masterViolations?.length || 0) > 0 ||
    (crossUnitFindings.compoundRuleViolations?.length || 0) > 0 ||
    (crossUnitFindings.globalVendorMaster?.proposal_count || 0) > 0
  );
  const isReconActive = activeUnitId === RECON_PILL_ID;
  const totalCrossUnitCount = crossUnitFindings ? (
    (crossUnitFindings.rollup?.orphans_findings || 0) +
    (crossUnitFindings.rollup?.master_violations_findings || 0) +
    (crossUnitFindings.rollup?.compound_violations_findings || 0) +
    (crossUnitFindings.rollup?.vendor_clusters_proposed || 0)
  ) : 0;

  const tabs: { id: TabId; label: string; count?: number }[] = isReconActive
    ? [
        { id: 'crossunit', label: 'Findings', count: totalCrossUnitCount > 0 ? totalCrossUnitCount : undefined },
        { id: 'questions', label: 'Questions', count: questions.filter(q => !q.applied && q.status !== 'APPLIED').length || undefined },
      ]
    : [
        { id: 'overview', label: 'Overview' },
        { id: 'issues', label: 'Issues', count: openIssueCount > 0 ? openIssueCount : undefined },
        { id: 'questions', label: 'Questions', count: questions.filter(q => !q.applied && q.status !== 'APPLIED').length || undefined },
        { id: 'changelog', label: 'Change Log', count: changeLog.length || undefined },
        { id: 'preview', label: 'Data Preview', count: livePreviewRows.length || undefined },
      ];

  const isProseSession = result?.pipeline === 'prose';
  const headerRowsTotal = isMultiUnit ? v2Result.rollup.total_rows : (activeUnit?.rows_total || result.stats?.totalRows || 0);
  const headerRowsAfter = isMultiUnit ? v2Result.rollup.total_rows_after : (activeUnit?.rows_after || result.stats?.rowsAfterCleaning || result.stats?.totalRows || 0);
  const proseStats = result?.stats || {};
  const proseFiles = v2Result.originalFileNames.join(', ') || activeUnit?.file_name || '';
  const headerLabel = isProseSession
    ? `${(proseStats.sectionsExtracted || 0).toLocaleString()} section${proseStats.sectionsExtracted === 1 ? '' : 's'} across ${(proseStats.documentsReviewed || v2Result.originalFileNames.length || 1).toLocaleString()} document${(proseStats.documentsReviewed || 1) === 1 ? '' : 's'} · ${proseFiles}`
    : isMultiUnit
      ? `${headerRowsTotal.toLocaleString()} rows across ${v2Result.units.length} sheets → ${headerRowsAfter.toLocaleString()} cleaned rows · ${v2Result.originalFileNames.join(', ')}`
      : (headerRowsTotal > 0
          ? `${headerRowsTotal.toLocaleString()} rows → ${headerRowsAfter.toLocaleString()} rows · ${proseFiles}`
          : '');

  const distinctFileCount = useMemo(() => new Set(v2Result.units.map(u => u.file_name)).size, [v2Result]);

  const unitLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of v2Result.units) {
      map[u.unit_id] = u.sheet_name ? `${u.file_name} › ${u.sheet_name}` : u.file_name;
    }
    return map;
  }, [v2Result]);

  useEffect(() => {
    if (isReconActive && activeTab !== 'crossunit' && activeTab !== 'questions') {
      setActiveTab('crossunit');
    } else if (!isReconActive && activeTab === 'crossunit') {
      setActiveTab('overview');
    }
  }, [isReconActive, activeTab]);

  const handleCrossUnitApply = useCallback((findingKind: string, finding: any) => {
    const findingId = finding?.finding_id || finding?.cluster_id;
    const targetQ = allQuestions.find((q: Question) => {
      if (q.question_type !== 'Q_CROSS_UNIT_RECONCILIATION') return false;
      const fj = q.finding;
      if (!fj) return false;
      return fj.finding_id === findingId || fj.cluster_id === findingId;
    });
    if (targetQ) {
      setActiveTab('questions');
      setHighlightQuestionId(targetQ.question_id);
    } else {
      toast.error('Resolution question for this finding is no longer available.');
    }
  }, [allQuestions]);

  const renderUnitPill = (unit: SheetUnit) => {
    const isActive = unit.unit_id === activeUnitId;
    const isFailed = unit.status === 'failed';
    const isPartial = unit.status === 'partial';
    const issueCount = (unit.issuesFound || []).filter((i: any) => i.status === 'needs_review' || i.status === 'cannot_fix').length;
    const dbQuestionCount = allQuestions.filter(q =>
      q.question_type !== 'Q_CROSS_UNIT_RECONCILIATION' &&
      (!q.unit_id || q.unit_id === unit.unit_id) &&
      !q.applied && q.status !== 'APPLIED'
    ).length;
    const questionCount = dbQuestionCount > 0 ? dbQuestionCount : (unit.stakeholderQuestions || []).length;
    const primary = unit.sheet_name || unit.file_name;
    const secondary = (distinctFileCount > 1 && unit.sheet_name) ? unit.file_name : '';
    const tooltip = unit.sheet_name ? `${unit.file_name} › ${unit.sheet_name}` : unit.file_name;
    return (
      <button
        key={unit.unit_id}
        onClick={() => setActiveUnitId(unit.unit_id)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] font-medium border transition-colors flex-shrink-0 ${
          isActive
            ? 'bg-[#0D9488] text-white border-[#0D9488] shadow-sm'
            : isFailed
              ? 'bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5] hover:bg-[#FECACA]'
              : isPartial
                ? 'bg-[#FFFBEB] text-[#92400E] border-[#FCD34D] hover:bg-[#FEF3C7]'
                : 'bg-white text-[#475569] border-[#E2E8F0] hover:bg-[#F1F5F9]'
        }`}
        title={isFailed && unit.error ? `${tooltip} — ${unit.error}` : isPartial ? `${tooltip} — Cleaning incomplete, original data kept` : tooltip}
      >
        <div className="flex flex-col items-start min-w-0">
          <span className="font-semibold whitespace-nowrap max-w-[180px] truncate">{primary}</span>
          {secondary && (
            <span className={`text-[10px] whitespace-nowrap max-w-[180px] truncate ${isActive ? 'text-white/80' : 'text-[#94A3B8]'}`}>{secondary}</span>
          )}
        </div>
        {(isFailed || isPartial) && <AlertTriangle className="w-3 h-3 flex-shrink-0" />}
        {!isFailed && !isPartial && issueCount > 0 && (
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${isActive ? 'bg-white/25 text-white' : 'bg-[#FEF3C7] text-[#92400E]'}`}>{issueCount}</span>
        )}
        {!isFailed && !isPartial && questionCount > 0 && (
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${isActive ? 'bg-white/25 text-white' : 'bg-[#DBEAFE] text-[#1E40AF]'}`}>?{questionCount}</span>
        )}
      </button>
    );
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] overflow-hidden">
      <div className="px-5 py-3 border-b border-[#E2E8F0] bg-[#FAFCFF]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-7 h-7 rounded-md bg-[#0D9488] flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <span className="text-[15px] font-semibold text-[#0F172A] block">Data Cleaning Complete</span>
              {headerLabel && (
                <p className="text-[12px] text-[#64748B] truncate" title={headerLabel}>{headerLabel}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {(activeUnit?.cleanedFileUrl || result.cleanedFileUrl) && (
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                aria-busy={isDownloading}
                title={isDownloading
                  ? 'Preparing download…'
                  : downloadFormat === 'xlsx'
                    ? 'Download all cleaned sheets as one Excel workbook'
                    : downloadFormat === 'csv'
                      ? 'Download cleaned CSV'
                      : downloadFormat === 'json'
                        ? 'Download reconciled state (JSON)'
                        : `Download cleaned document (.${downloadFormat})`}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-white rounded-lg transition-colors whitespace-nowrap ${
                  isDownloading
                    ? 'bg-[#94A3B8] cursor-not-allowed'
                    : 'bg-[#0D9488] hover:bg-[#0B7A70]'
                }`}
              >
                {isDownloading
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing…</>
                  : <><Download className="w-3.5 h-3.5" /> Download (.{downloadFormat})</>}
              </button>
            )}
            <button onClick={onSendToSmartAnalyst} className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium bg-[#7C3AED] text-white rounded-lg hover:bg-[#6D28D9] transition-colors whitespace-nowrap">
              <ArrowRight className="w-3.5 h-3.5" /> Smart Data Analyst
            </button>
            <button onClick={onSendToCouncil} className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium bg-transparent text-[#64748B] border border-[#E2E8F0] rounded-lg hover:bg-[#F1F5F9] transition-colors whitespace-nowrap">
              <ExternalLink className="w-3.5 h-3.5" /> AI Council
            </button>
            <button onClick={onToggleExpand} className="p-1.5 hover:bg-[#F1F5F9] text-[#64748B] rounded-lg transition-colors ml-1 flex-shrink-0">
              {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Expand className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {(isMultiUnit || hasCrossUnitFindings) && (
          <div className="flex items-center gap-2 mt-3">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] flex-shrink-0">Sheet</span>
            <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-thin pb-1 -mb-1 flex-1">
              {hasCrossUnitFindings && (
                <button
                  key={RECON_PILL_ID}
                  onClick={() => setActiveUnitId(RECON_PILL_ID)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] font-medium border transition-colors flex-shrink-0 ${
                    isReconActive
                      ? 'bg-[#7C3AED] text-white border-[#7C3AED] shadow-sm'
                      : 'bg-white text-[#7C3AED] border-[#C4B5FD] hover:bg-[#F5F3FF]'
                  }`}
                  title="Cross-sheet reconciliation findings"
                >
                  <span className="font-semibold whitespace-nowrap">Reconciliation</span>
                  {totalCrossUnitCount > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold flex-shrink-0 ${isReconActive ? 'bg-white/25 text-white' : 'bg-[#FEF3C7] text-[#92400E]'}`}>{totalCrossUnitCount}</span>
                  )}
                </button>
              )}
              {v2Result.units.map(renderUnitPill)}
            </div>
          </div>
        )}

        <div className="flex gap-0.5 mt-3 border-b border-[#E2E8F0] -mb-[1px]">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-[13px] font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'text-[#0D9488] border-[#0D9488] bg-white'
                  : 'text-[#64748B] border-transparent hover:text-[#0F172A] hover:bg-[#F1F5F9]'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[11px] font-bold bg-[#FEF3C7] text-[#92400E]">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className={`p-5 overflow-y-auto scrollbar-thin ${isExpanded ? 'h-[calc(100vh-180px)]' : 'max-h-[600px]'}`}>
        {activeUnit?.status === 'failed' && (
          <div className="mb-4 bg-[#FEE2E2] border border-[#FCA5A5] rounded-lg px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-[#991B1B] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[13px] font-semibold text-[#991B1B]">Cleaning failed for this sheet</p>
                {activeUnit.error && (
                  <p className="text-[12px] text-[#991B1B] mt-1">{activeUnit.error}</p>
                )}
                {activeUnit.stderr_tail && (
                  <pre className="mt-2 text-[11px] text-[#7F1D1D] bg-white rounded p-2 max-h-32 overflow-auto whitespace-pre-wrap">{activeUnit.stderr_tail}</pre>
                )}
              </div>
            </div>
          </div>
        )}
        {activeUnit?.status === 'partial' && (
          <div className="mb-4 bg-[#FFFBEB] border border-[#FCD34D] rounded-lg px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-[#92400E] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[13px] font-semibold text-[#92400E]">Cleaning incomplete — original data passed through</p>
                <p className="text-[12px] text-[#92400E] mt-1">The AI cleaning script did not complete for this sheet. The original data has been kept so other operations (union, join) can still proceed. Please review this file manually.</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'crossunit' && crossUnitFindings && (
          <CrossUnitTab
            findings={crossUnitFindings}
            unitLabelById={unitLabelById}
            onApplyFinding={handleCrossUnitApply}
          />
        )}

        {activeTab === 'overview' && (
          <OverviewTab
            qualityScore={qualityScore}
            summary={v2Result.summary || result.summary || ''}
            stats={isProseSession ? (result?.stats || {}) : (unitOrLegacy(v2Result, activeUnitId).stats || {})}
            pipelinePhases={pipelinePhases}
            issues={issues}
            questions={questions}
            onNavigateToIssue={navigateToIssue}
            onNavigateToQuestion={navigateToQuestion}
            isProse={isProseSession}
            crossUnitFindings={crossUnitFindings}
            onOpenReconciliation={hasCrossUnitFindings ? () => { setActiveUnitId(RECON_PILL_ID); setActiveTab('crossunit'); } : undefined}
          />
        )}

        {activeTab === 'issues' && (
          <IssuesTable
            issues={issues}
            onNavigateToQuestion={navigateToQuestion}
            onNavigateToDataPreview={(issueId) => setActiveTab('preview')}
          />
        )}

        {activeTab === 'questions' && (
          <QuestionsPanel
            questions={questions}
            onApply={handleApplyQuestion}
            onPreview={handlePreviewQuestion}
            onNavigateToIssue={navigateToIssue}
            highlightQuestionId={highlightQuestionId}
            availableColumns={availableColumns}
            sectionsByFile={result?.sectionsByFile || {}}
          />
        )}

        {activeTab === 'changelog' && (
          <ChangeLogTab entries={changeLog} />
        )}

        {activeTab === 'preview' && (
          <DataPreviewTab
            previewBefore={activeUnit?.previewBefore || result.previewBefore || ''}
            previewAfter={activeUnit?.previewAfter || result.previewAfter || ''}
            rows={previewRows}
            stats={unitOrLegacy(v2Result, activeUnitId).stats || {}}
            proseDocuments={result?.pipeline === 'prose' ? proseDocuments : null}
          />
        )}
      </div>
    </div>
  );
};

export default CleaningResultsPanel;
